import { FieldValue, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CAM_LICENSE_END_DATE,
  CAM_LICENSE_NO,
  CAM_LICENSE_START_DATE,
  FILLING_DUE_DATE,
  LAST_AGM_DATE,
  STATE_TAX_ID,
  FED_TAX_ID,
  formatFederalTaxId,
} from "./consts";
import { toast } from "@/components/Toast";
import {
  useGetCommunityLicenseInfo,
  useUpdateLicenseInfoMutation,
} from "@/services";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { routes } from "@/routes/utils";
import { getRequiredFields, removeEmptyFields, setEmptyFieldsToNull } from "@/lib/utils";
import moment from "moment";
import { licenseSchema } from "./validations";

const useStep2 = ({ onNextClick }: { onNextClick?: () => void }) => {
  const { id, action } = useParams();
  const form = useForm<any>({
    defaultValues: {
      [FED_TAX_ID]: undefined,
      [STATE_TAX_ID]: undefined,
      [CAM_LICENSE_NO]: undefined,
      [CAM_LICENSE_START_DATE]: undefined,
      [CAM_LICENSE_END_DATE]: undefined,
      [LAST_AGM_DATE]: undefined,
      [FILLING_DUE_DATE]: undefined,
    },
    resolver: zodResolver(licenseSchema),
  });
  const navigate = useNavigate();
  const isEditDetails = action === "edit-details";
  const isAdd = action == "add";

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(licenseSchema)

  const {
    data: communityLicenseInfo,
    isLoading: isLicenseInfoLoading,
    isRefetching: isLicenseInfoRefetching,
  } = useGetCommunityLicenseInfo({
    community_id: id || "",
    queryParams: {
      enabled: !!(id && !isAdd),
    },
  });

  useEffect(() => {
    if (communityLicenseInfo) {
      form.reset({
        [FED_TAX_ID]: communityLicenseInfo?.data?.[FED_TAX_ID]
          ? formatFederalTaxId(communityLicenseInfo?.data?.[FED_TAX_ID])
          : undefined,
        [STATE_TAX_ID]: communityLicenseInfo?.data?.[STATE_TAX_ID]
          ? formatFederalTaxId(communityLicenseInfo?.data?.[STATE_TAX_ID])
          : undefined,
        [CAM_LICENSE_NO]:
          communityLicenseInfo?.data?.camLicenseNumber || undefined,
        [CAM_LICENSE_START_DATE]:
          communityLicenseInfo?.data?.[CAM_LICENSE_START_DATE] || undefined,
        [CAM_LICENSE_END_DATE]:
          communityLicenseInfo?.data?.[CAM_LICENSE_END_DATE] || undefined,
        [LAST_AGM_DATE]:
          communityLicenseInfo?.data?.[LAST_AGM_DATE] || undefined,
        [FILLING_DUE_DATE]:
          communityLicenseInfo?.data?.[FILLING_DUE_DATE] || undefined,
      });
    }
  }, [communityLicenseInfo]);

  const { mutate: updateInfo, isPending: isUpdatePending } =
    useUpdateLicenseInfoMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        if (isEditDetails) {
          navigate(
            `/${routes.cam}/${routes.communities}/${id}/${routes.details}`
          );
        } else {
          onNextClick && onNextClick();
        }
        toast.success("Info Updated Successfully");
      },
    });

  const onSubmit = (data: FieldValue<any>) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (_data[CAM_LICENSE_NO]) {
      if (!_data[CAM_LICENSE_START_DATE]) {
        form.setError(CAM_LICENSE_START_DATE, {
          message: "CAM License Start Date is required",
        });
        return;
      }
      if (!_data[CAM_LICENSE_END_DATE]) {
        form.setError(CAM_LICENSE_END_DATE, {
          message: "CAM License End Date is required",
        });
        return;
      }
      if (
        moment(_data[CAM_LICENSE_START_DATE]).isAfter(
          moment(_data[CAM_LICENSE_END_DATE])
        )
      ) {
        form.setError(CAM_LICENSE_END_DATE, {
          message: "End Date should grater than Start Date",
        });
        return;
      }
    } else {
      _data = {
        ..._data,
        [CAM_LICENSE_NO]: null,
        [CAM_LICENSE_END_DATE]: null,
        [CAM_LICENSE_START_DATE]: null,
      };
    }

    updateInfo({ id: id || "", data: _data });
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isUpdatePending,
    isEditDetails,
    id,
    isLicenseInfoLoading,
    isLicenseInfoRefetching,
  };
};

export default useStep2;
